'use client'

import { useState } from 'react'
import { Button } from '@synthec/ui'
import type { Locale } from '@synthec/types'

const field =
  'w-full rounded-xl border border-ink-200 bg-white px-4 py-3 text-sm text-ink-900 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20'

export function ContactForm({
  locale,
  producto,
  pagePath,
}: {
  locale: Locale
  /** Prefill + tag the lead when arriving from a "Solicitar cotización" CTA. */
  producto?: string
  pagePath?: string
}) {
  const [status, setStatus] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle')

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const formEl = event.currentTarget
    setStatus('sending')
    try {
      const payload = Object.fromEntries(new FormData(formEl).entries())
      // Trailing slash: the site runs trailingSlash:true, so /api/contact would
      // 308-redirect here. Post to the canonical URL directly — no redirect hop
      // on a lead submission.
      const res = await fetch('/api/contact/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (res.ok) {
        formEl.reset()
        setStatus('ok')
      } else {
        setStatus('error')
      }
    } catch {
      setStatus('error')
    }
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
      {/* Routing context (country/source/origin) — not shown to the user. */}
      <input type="hidden" name="locale" value={locale} />
      {producto ? <input type="hidden" name="producto" value={producto} /> : null}
      {pagePath ? <input type="hidden" name="pagePath" value={pagePath} /> : null}
      {/* Honeypot: hidden from users; bots that fill it are silently dropped. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />
      <input name="nombre" required placeholder="Nombre" className={field} />
      <input name="empresa" placeholder="Empresa" className={field} />
      <input name="email" type="email" required placeholder="Correo" className={field} />
      <input name="telefono" placeholder="Teléfono" className={field} />
      <textarea
        name="mensaje"
        required
        rows={5}
        placeholder="¿En qué podemos ayudarte?"
        defaultValue={producto ? `Hola, quisiera cotizar: ${producto}.` : undefined}
        className={`${field} sm:col-span-2`}
      />
      <div className="flex items-center gap-4 sm:col-span-2">
        <Button type="submit" disabled={status === 'sending'}>
          {status === 'sending' ? 'Enviando…' : 'Enviar'}
        </Button>
        {status === 'ok' && <span className="text-sm text-brand">Gracias, te contactaremos pronto.</span>}
        {status === 'error' && <span className="text-sm text-accent">Hubo un error. Intenta de nuevo.</span>}
      </div>
    </form>
  )
}
